import React, { useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc, updateDoc, getDocs, query, collection, where, documentId } from "firebase/firestore";
import { useAuth } from "../../AuthContext";
import { findOurBarNear, geohash } from "./ourBarNear";
import { savedRef } from "./partnerRef";

export default function ClaimBar() {
  const { user, role, userBars, refreshUserBars } = useAuth();
  const roleField = role === "owner" ? "ownerId" : role === "charity" ? "charityId" : null;

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [error, setError] = useState("");

  const myBars = userBars.filter((bar) => bar[roleField] === user?.uid);

  const runSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      setError("Google Maps failed to load. Try refreshing the page.");
      return;
    }

    setSearching(true);
    setError("");

    const map = new window.google.maps.Map(document.createElement("div"));
    const service = new window.google.maps.places.PlacesService(map);

    service.textSearch(
      {
        query: `${search} bar OR pub OR tavern OR restaurant OR bingo OR casino`,
      },
      async (places, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !places) {
          setResults([]);
          setSearching(false);
          return;
        }

        const placeIds = places.map((p) => p.place_id);

        let existingDocs = {};
        // Check every result (10 at a time), not just the first 10.
        for (let i = 0; i < placeIds.length; i += 10) {
          const q = query(collection(db, "bars"), where(documentId(), "in", placeIds.slice(i, i + 10)));
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            existingDocs[d.id] = d.data();
          });
        }

        // Google gives its own ids. If the bar is already in the app under a
        // different id (e.g. from the national list), use OUR bar instead so
        // claiming never makes a duplicate.
        const rows = await Promise.all(
          places.map(async (p) => {
            const lat = p.geometry?.location ? p.geometry.location.lat() : null;
            const lng = p.geometry?.location ? p.geometry.location.lng() : null;
            let id = p.place_id;
            let existing = existingDocs[p.place_id];
            if (!existing) {
              const ours = await findOurBarNear(p.name, lat, lng).catch(() => null);
              if (ours) {
                id = ours.id;
                existing = ours.data;
              }
            }
            return {
              place_id: id,
              name: existing?.name || p.name,
              address: existing?.address || p.formatted_address,
              lat,
              lng,
              ownerId: existing?.ownerId || "",
              charityId: existing?.charityId || "",
            };
          })
        );
        const seen = new Set();
        const merged = rows
          .filter((p) => (seen.has(p.place_id) ? false : seen.add(p.place_id)))
          // Hide bars you already have. Bars someone else (same role) already
          // claimed stay visible but can't be claimed again (see below).
          .filter((p) => p[roleField] !== user?.uid);

        setResults(merged);
        setSearching(false);
      }
    );
  };

  const claimBar = async (place) => {
    if (!user || !roleField) return;
    setClaimingId(place.place_id);
    setError("");

    try {
      const barRef = doc(db, "bars", place.place_id);
      const barSnap = await getDoc(barRef);
      // When it was claimed, and which sign-up link/QR (if any) led here.
      const ref = savedRef();
      const claimInfo = {
        [`${roleField}ClaimedAt`]: new Date(),
        ...(ref ? { [`${roleField}ClaimRef`]: ref } : {}),
      };

      // Never take over a bar another account in the same role already claimed.
      const takenBy = barSnap.exists() ? barSnap.data()[roleField] : "";
      if (takenBy && takenBy !== user.uid) {
        setError(
          role === "owner"
            ? "This bar is already claimed by another owner. Contact us at hello@barfunfinder.com if it's yours."
            : "This venue is already linked to another charity. Contact us at hello@barfunfinder.com if that's wrong."
        );
        setClaimingId(null);
        return;
      }

      if (!barSnap.exists()) {
        const gh5 = place.lat != null && place.lng != null ? geohash(place.lat, place.lng, 5) : "";
        await setDoc(barRef, {
          name: place.name,
          address: place.address,
          lat: place.lat,
          lng: place.lng,
          gh5,
          gh4: gh5.slice(0, 4),
          legacyGh5: gh5,
          state: "",
          zip: "",
          phone: "",
          photoURL: "",
          birthdayDeals: [],
          specials: [],
          gamingInventory: [],
          ownerId: role === "owner" ? user.uid : "",
          charityId: role === "charity" ? user.uid : "",
          ...claimInfo,
        });
      } else {
        await updateDoc(barRef, { [roleField]: user.uid, ...claimInfo });
      }

      setResults((prev) => prev.filter((p) => p.place_id !== place.place_id));
      await refreshUserBars();
    } catch (err) {
      console.error(err);
      setError("Failed to claim this bar. Try again.");
    }

    setClaimingId(null);
  };

  const unclaimBar = async (barId) => {
    const bar = myBars.find((b) => b.id === barId);
    const ok = window.confirm(
      role === "owner"
        ? `Unclaim ${bar?.name || "this bar"}? You'll no longer manage it, and it will show as unclaimed until someone claims it again.`
        : `Unlink ${bar?.name || "this bar"}? Your charity will no longer be shown as running gaming there.`
    );
    if (!ok) return;
    const ref = doc(db, "bars", barId);
    await updateDoc(ref, { [roleField]: "" });
    await refreshUserBars();
  };

  if (!roleField) {
    return <p style={{ color: "#fff" }}>Only owners and charities can claim bars.</p>;
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "10px" }}>
        {role === "owner" ? "Claim Your Bar" : "Claim a Venue for Gaming"}
      </h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        Search any bar, tavern, restaurant, or gaming venue — even if it's not in our system yet.
      </p>

      <form onSubmit={runSearch} style={{ display: "flex", gap: "10px", marginBottom: "24px", maxWidth: "600px" }}>
        <input
          type="text"
          placeholder="Search by name or city"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, padding: "14px", borderRadius: "8px", border: "none", fontSize: "18px" }}
        />
        <button
          type="submit"
          disabled={searching}
          style={{
            background: "linear-gradient(90deg, #6D28D9, #A855F7)",
            border: "none",
            padding: "12px 20px",
            borderRadius: "8px",
            color: "#fff",
            fontWeight: 600,
            fontSize: "16px",
            cursor: "pointer",
          }}
        >
          {searching ? "Searching..." : "Search"}
        </button>
      </form>

      {error && <p style={{ color: "#F472B6", marginBottom: "16px" }}>{error}</p>}

      {results.length > 0 && (
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{ marginBottom: "10px" }}>Search Results</h3>
          {results.map((place) => (
            <div
              key={place.place_id}
              style={{
                background: "#0D0B1A",
                border: "1px solid rgba(255,255,255,0.08)",
                padding: "15px",
                borderRadius: "10px",
                marginBottom: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontSize: "19px" }}>{place.name}</h4>
                <p style={{ margin: "4px 0 0", color: "#B8B0C8", fontSize: "15px" }}>{place.address}</p>
              </div>
              {place[roleField] && place[roleField] !== user?.uid ? (
                <span style={{ color: "#B8B0C8", fontSize: "14px", maxWidth: "220px", textAlign: "right" }}>
                  {role === "owner" ? "Already claimed by another owner." : "Already linked to another charity."}{" "}
                  <a href="mailto:hello@barfunfinder.com" style={{ color: "#B79BFF" }}>Contact us</a> if this is wrong.
                </span>
              ) : (
              <button
                onClick={() => claimBar(place)}
                disabled={claimingId === place.place_id}
                style={{
                  background: "linear-gradient(90deg, #6D28D9, #A855F7)",
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "15px",
                  cursor: "pointer",
                }}
              >
                {claimingId === place.place_id ? "Claiming..." : "Claim This Bar"}
              </button>
              )}
            </div>
          ))}
        </div>
      )}

      <h3 style={{ marginBottom: "10px" }}>Your Linked Bars</h3>
      {myBars.length === 0 ? (
        <p>You haven't claimed any bars yet.</p>
      ) : (
        myBars.map((bar) => (
          <div
            key={bar.id}
            style={{
              background: "#0D0B1A",
              border: "1px solid rgba(168,85,247,0.4)",
              padding: "15px",
              borderRadius: "10px",
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontSize: "19px" }}>{bar.name || "Unnamed Bar"}</h4>
              <p style={{ margin: "4px 0 0", color: "#B8B0C8", fontSize: "15px" }}>{bar.address}</p>
            </div>
            <button
              onClick={() => unclaimBar(bar.id)}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.3)",
                padding: "10px 16px",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "15px",
                cursor: "pointer",
              }}
            >
              Unclaim
            </button>
          </div>
        ))
      )}
    </div>
  );
}
